import { JSX, useEffect, useState } from 'react';
import CreateOshaReportStepOne from '../../views/CreateOshaReportStepOne';
import { useFormik } from 'formik';
import { getCreateOshaReportSchema } from './schema';
import CreateOshaReportStepTwo from '../../views/CreateOshaReportStepTwo';
import CreateOshaReportStepThree from '../../views/CreateOshaReportStepThree';
import CreateOshaReportStepFour from '../../views/CreateOshaReportStepFour';
import { useLocation } from 'react-router';
import { useApiClients } from '../../client/api/useApiClient';
import { useQuery } from '@tanstack/react-query';
import {
  GetEstablishmentResponseDto,
  GetReportsForOshaResponse,
  GetYearStatusForOshaSubmissionResponse,
  InvestigationWithAffectedBodyPartsSentence,
} from '@EHSNavigator/backend';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import AlreadySubmittedToOsha from '../../components/AlreadySubmittedToOsha';
import { hideBodyScroll, showBodyScroll } from '../../utils/bodyScroll';
import OSHANotAvailable from '../../components/OSHANotAvailable';
import { dateOshaValidation } from '../../utils/oshaDateValidation';

export interface CreateOshaReportFormValues {
  annualAverageNumberOfEmployees: string;
  annualAverageNumberOfEmployeesWorkingHours: string;
}
export default function CreateOshaReportPage() {
  const { state: routerState = {} } = useLocation();
  const year = routerState?.year || '2024';
  const { OshaReportingApi, EstablishmentApi } = useApiClients();

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [state, setState] = useState<{
    excludedRow: string[];
  }>({
    excludedRow: [],
  });

  const { data, isLoading, isRefetching } = useQuery<
    GetReportsForOshaResponse,
    Error,
    any
  >({
    queryKey: ['get-reports'],
    queryFn: async () => {
      const response =
        await OshaReportingApi.oshaReportingControllerGetReportsForOsha({
          year: Number(year),
        });
      return response?.data;
    },
  });
  const {
    data: oshaGenerateStatus,
    isLoading: isOshaGenerateStatusLoading,
    isRefetching: isOshaGenerateStatusRefetching,
  } = useQuery<
    GetYearStatusForOshaSubmissionResponse['hasSubmitted'],
    Error,
    any
  >({
    queryKey: ['get-osha-reporting-status'],
    queryFn: async () => {
      const response =
        await OshaReportingApi.oshaReportingControllerGetYearStatusForOshaSubmission(
          {
            year: Number(year),
          },
        );
      return response?.data?.hasSubmitted;
    },
  });

  const filterOutReports = data?.reports?.filter(
    (node: InvestigationWithAffectedBodyPartsSentence) =>
      !state?.excludedRow?.includes(node?.id),
  );

  const createOshaReportHandler = useFormik<CreateOshaReportFormValues>({
    enableReinitialize: true,
    initialValues: {
      annualAverageNumberOfEmployees: '',
      annualAverageNumberOfEmployeesWorkingHours: '',
    },
    validationSchema: getCreateOshaReportSchema(currentStep, filterOutReports),

    onSubmit: async (values) => {
      try {
        const res = await OshaReportingApi.oshaReportingControllerCreate({
          submitInvestigationsToOshaRequest: {
            annualAverageEmployees: Number(
              values?.annualAverageNumberOfEmployees,
            ),
            totalHoursWorked: Number(
              values?.annualAverageNumberOfEmployeesWorkingHours,
            ),
            year: 2024,
            investigationIds: filterOutReports?.map(
              (node: InvestigationWithAffectedBodyPartsSentence) => node?.id,
            ),
          },
        });
        toast.success(res?.data?.message);
        setCurrentStep(4);
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const handleNextStep = async (steps: number) => {
    if (steps < currentStep) {
      setCurrentStep(steps);
      window.scrollTo(0, 0);
    } else {
      const errors = await createOshaReportHandler.validateForm();
      if (Object.keys(errors).length === 0) {
        window.scrollTo(0, 0);
        setCurrentStep(steps);
      } else {
        const touchedFields = Object.keys(errors).reduce((acc: any, key) => {
          acc[key] = true;
          return acc;
        }, {});

        createOshaReportHandler.setTouched(touchedFields);
      }
    }
  };

  const { data: eData } = useQuery<
    GetEstablishmentResponseDto['establishment'],
    Error,
    any
  >({
    queryKey: ['get-establishment'],
    queryFn: async () => {
      const response =
        await EstablishmentApi.establishmentControllerGetEstablishment();
      return response?.data?.establishment;
    },
  });

  const steps: Record<number, JSX.Element> = {
    1: (
      <CreateOshaReportStepOne
        handleExcludeRow={setState}
        excludedRow={state.excludedRow}
        handleSteps={handleNextStep}
        data={data}
        year={year}
      />
    ),
    2: (
      <CreateOshaReportStepTwo
        handleSteps={handleNextStep}
        data={filterOutReports}
      />
    ),
    3: (
      <CreateOshaReportStepThree
        handleSteps={handleNextStep}
        formHandler={createOshaReportHandler}
      />
    ),
    4: <CreateOshaReportStepFour establishment={eData} />,
  };

  const isDateAvailable = dateOshaValidation();

  useEffect(() => {
    const handleScroll = () => {
      if (oshaGenerateStatus || !isDateAvailable) {
        hideBodyScroll();
      } else {
        showBodyScroll();
      }
    };

    handleScroll();
    return () => {
      showBodyScroll();
    };
  }, [oshaGenerateStatus, isDateAvailable]);

  if (
    isLoading ||
    isRefetching ||
    isOshaGenerateStatusLoading ||
    isOshaGenerateStatusRefetching
  ) {
    return <Loader />;
  }

  return (
    <div className="relative flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      <form
        action=""
        className="flex h-full w-full flex-col"
        onSubmit={createOshaReportHandler.handleSubmit}
      >
        {steps[currentStep]}
      </form>
      {isDateAvailable && oshaGenerateStatus && (
        <AlreadySubmittedToOsha currentYear={year} />
      )}
      {!isDateAvailable && <OSHANotAvailable />}
    </div>
  );
}
